// The paket data of a Non-Penyedia PBJ project (migrations 0023, 0025), one
// set of fields for Buat project (PbjCreateFields) and Ubah paket
// (PaketDialog): the route with its method or Swakelola type, the unit kerja
// (instansi), jenis pengadaan, tahun anggaran, kode RUP and pagu. The form
// keeps text; paketInput turns it into the API's fields.
import { useId } from "react";
import type { JenisPengadaan, PbjCreateInput, PbjMethod, PbjProject, PbjRoute, SwakelolaType } from "../api";
import { formatCount } from "../lib/format";
import { JENIS_LABEL } from "../project/stages";
import { Field } from "../ui/components";
import { PBJ_METHOD_LABEL, PBJ_METHOD_ORDER, PBJ_ROUTE_LABEL, SWAKELOLA_LABEL, SWAKELOLA_ORDER } from "./labels";

export interface PaketForm {
  route: PbjRoute;
  method: PbjMethod;
  swakelolaType: SwakelolaType;
  instansi: string;
  jenis: JenisPengadaan | "";
  /** Four digits, or empty. */
  tahun: string;
  kodeRup: string;
  /** Digits only. */
  pagu: string;
  /** Keterangan paket, free text. */
  keterangan: string;
}

export const TAHUN_MIN = 2000;
export const TAHUN_MAX = 2100;

export function defaultPaketForm(year: number = new Date().getFullYear()): PaketForm {
  return { route: "penyedia", method: "tender_seleksi", swakelolaType: "tipe_1", instansi: "", jenis: "", tahun: String(year), kodeRup: "", pagu: "", keterangan: "" };
}

/** The stored project as a form (Ubah paket opens on it). */
export function paketFormOf(p: PbjProject): PaketForm {
  const base = defaultPaketForm();
  return {
    route: p.route,
    method: p.method ?? base.method,
    swakelolaType: p.swakelolaType ?? base.swakelolaType,
    instansi: p.instansi ?? "",
    jenis: p.jenisPengadaan ?? "",
    tahun: p.tahunAnggaran == null ? "" : String(p.tahunAnggaran),
    kodeRup: p.kodeRup ?? "",
    pagu: p.pagu == null ? "" : String(Math.round(p.pagu)),
    keterangan: p.keterangan ?? "",
  };
}

const squash = (v: string) => v.replace(/\s+/g, " ").trim();

/** A typed year the API takes, else null. */
export function tahunOf(text: string): number | null {
  const n = Number(text);
  return /^\d{4}$/.test(text) && n >= TAHUN_MIN && n <= TAHUN_MAX ? n : null;
}

/** The API fields of the form (empty text is null). */
export function paketInput(f: PaketForm): Omit<PbjCreateInput, "name"> {
  return {
    route: f.route,
    method: f.route === "penyedia" ? f.method : null,
    swakelolaType: f.route === "swakelola" ? f.swakelolaType : null,
    instansi: squash(f.instansi) || null,
    jenisPengadaan: f.jenis || null,
    tahunAnggaran: tahunOf(f.tahun),
    kodeRup: squash(f.kodeRup) || null,
    pagu: f.pagu ? Number(f.pagu) : null,
    keterangan: f.keterangan.trim() || null,
  };
}

/** A year field that is not empty and not a year the API takes. */
export function tahunError(f: PaketForm): string | null {
  return f.tahun && tahunOf(f.tahun) === null ? `Tahun anggaran ${TAHUN_MIN} sampai ${TAHUN_MAX}` : null;
}

const digits = (v: string, max: number) => v.replace(/[^\d]/g, "").replace(/^0+(?=\d)/, "").slice(0, max);

/**
 * The typed year: its last four digits, so typing on after a whole or a
 * partial year ("2026" then "2027", or "2026" with "6" replaced by "7")
 * keeps what was typed last instead of the first four digits.
 */
export function tahunText(v: string): string {
  return v.replace(/[^\d]/g, "").slice(-4);
}

export interface PaketFieldsProps {
  value: PaketForm;
  onChange: (next: PaketForm) => void;
  disabled?: boolean;
  /** Under the method or type: what a change of route does (Ubah paket). */
  routeHint?: string;
}

export function PaketFields({ value, onChange, disabled, routeHint }: PaketFieldsProps) {
  const id = useId();
  const set = (patch: Partial<PaketForm>) => onChange({ ...value, ...patch });
  const yearError = tahunError(value);
  return (
    <>
      <fieldset className="pbj-choice" disabled={disabled}>
        <legend className="pbj-legend">Jalur</legend>
        {(["penyedia", "swakelola"] as const).map((route) => (
          <label key={route} className="pbj-option pbj-option-inline">
            <input type="radio" name={`${id}-route`} checked={value.route === route} onChange={() => set({ route })} />
            <span className="pbj-option-title">{PBJ_ROUTE_LABEL[route]}</span>
          </label>
        ))}
      </fieldset>
      {value.route === "penyedia" ? (
        <Field id={`${id}-method`} label="Metode pemilihan" hint={routeHint}>
          <select id={`${id}-method`} value={value.method} disabled={disabled} aria-describedby={`${id}-method-hint`} onChange={(e) => set({ method: e.currentTarget.value as PbjMethod })}>
            {PBJ_METHOD_ORDER.map((m) => (
              <option key={m} value={m}>
                {PBJ_METHOD_LABEL[m]}
              </option>
            ))}
          </select>
        </Field>
      ) : (
        <Field id={`${id}-type`} label="Tipe Swakelola" hint={routeHint}>
          <select id={`${id}-type`} value={value.swakelolaType} disabled={disabled} aria-describedby={`${id}-type-hint`} onChange={(e) => set({ swakelolaType: e.currentTarget.value as SwakelolaType })}>
            {SWAKELOLA_ORDER.map((t) => (
              <option key={t} value={t}>
                {SWAKELOLA_LABEL[t]}
              </option>
            ))}
          </select>
        </Field>
      )}
      <Field id={`${id}-instansi`} label="Unit kerja / instansi (opsional)">
        <input id={`${id}-instansi`} autoComplete="organization" maxLength={200} value={value.instansi} readOnly={disabled} onChange={(e) => set({ instansi: e.currentTarget.value })} />
      </Field>
      <Field id={`${id}-jenis`} label="Jenis pengadaan">
        <select id={`${id}-jenis`} value={value.jenis} disabled={disabled} onChange={(e) => set({ jenis: e.currentTarget.value as PaketForm["jenis"] })}>
          <option value="">Belum dipilih</option>
          {(Object.keys(JENIS_LABEL) as JenisPengadaan[]).map((k) => (
            <option key={k} value={k}>
              {JENIS_LABEL[k]}
            </option>
          ))}
        </select>
      </Field>
      <div className="pk-form-row">
        <Field id={`${id}-tahun`} label="Tahun anggaran" error={yearError}>
          <input
            id={`${id}-tahun`}
            inputMode="numeric"
            autoComplete="off"
            placeholder={String(new Date().getFullYear())}
            value={value.tahun}
            readOnly={disabled}
            aria-describedby={`${id}-tahun-hint`}
            aria-invalid={yearError ? true : undefined}
            // Focus selects the year, so a new one replaces it whole.
            onFocus={(e) => e.currentTarget.select()}
            onChange={(e) => set({ tahun: tahunText(e.currentTarget.value) })}
          />
        </Field>
        <Field id={`${id}-rup`} label="Kode RUP (opsional)">
          <input id={`${id}-rup`} autoComplete="off" maxLength={60} value={value.kodeRup} readOnly={disabled} onChange={(e) => set({ kodeRup: e.currentTarget.value })} />
        </Field>
      </div>
      <Field id={`${id}-pagu`} label="Pagu anggaran (Rp, opsional)">
        <input
          id={`${id}-pagu`}
          inputMode="numeric"
          autoComplete="off"
          placeholder="mis. 500.000.000"
          value={value.pagu ? formatCount(Number(value.pagu)) : ""}
          readOnly={disabled}
          onChange={(e) => set({ pagu: digits(e.currentTarget.value, 15) })}
        />
      </Field>
      <Field id={`${id}-ket`} label="Keterangan paket (opsional)">
        <textarea
          id={`${id}-ket`}
          rows={3}
          maxLength={2000}
          placeholder="mis. latar belakang kebutuhan, lokasi, atau catatan pelaksanaan"
          value={value.keterangan}
          readOnly={disabled}
          onChange={(e) => set({ keterangan: e.currentTarget.value })}
        />
      </Field>
    </>
  );
}
